import { useState } from 'react';
import type { NoteEntry } from '../types';

/** A task's running notes: an append-only list (who / when / what), plus an
 *  add-a-line box for people allowed to comment. Used by both Goals & Tasks and
 *  Initiatives. */
export default function TaskNotes({
  notes,
  legacyNote,
  canAdd,
  onAdd,
}: {
  notes: NoteEntry[] | undefined;
  /** Old pre-noteLog single blob, shown first if present. */
  legacyNote?: string | null;
  canAdd: boolean;
  onAdd: (text: string) => Promise<void>;
}) {
  const [text, setText] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const list = notes ?? [];

  return (
    <div style={{ fontSize: 12, marginTop: 4 }}>
      {legacyNote && <div style={{ color: 'var(--muted)' }}>· {legacyNote}</div>}
      {list.map((n, i) => (
        <div key={i} style={{ padding: '2px 0', color: 'var(--muted)' }}>
          <span style={{ color: 'var(--ink)' }}>{n.text}</span>{' '}
          — {n.by.split('@')[0]}, {fmtWhen(n.at)}
        </div>
      ))}
      {list.length === 0 && !legacyNote && (
        <div style={{ color: 'var(--muted)' }}>No notes yet.</div>
      )}
      {canAdd && (
        <>
          <div style={{ display: 'flex', gap: 6, marginTop: 4 }}>
            <input
              value={text}
              onChange={(e) => setText(e.target.value)}
              placeholder="Add a note"
              style={{ flex: 1 }}
            />
            <button
              className="btn"
              disabled={busy || !text.trim()}
              onClick={async () => {
                setBusy(true);
                setErr(null);
                try {
                  await onAdd(text.trim());
                  setText('');
                } catch (e) {
                  setErr(e instanceof Error ? e.message : 'Could not add that note.');
                } finally {
                  setBusy(false);
                }
              }}
            >
              Add
            </button>
          </div>
          {err && <div className="err">{err}</div>}
        </>
      )}
    </div>
  );
}

function fmtWhen(at: string): string {
  const d = new Date(at);
  if (Number.isNaN(d.getTime())) return at;
  return d.toLocaleDateString(undefined, {
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });
}
